import { useEffect, useState } from "react";
import BottomSheet from "./BottomSheet";
import { ALL_TRACKS, RACE_NUMBERS_ALL } from "../lib/constants";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function CompetitionSettingsModal({ open, onClose, group, onSaved }) {
  const [weeks, setWeeks] = useState(10);
  const [startDate, setStartDate] = useState("");
  const [tracks, setTracks] = useState([]);
  const [raceNums, setRaceNums] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && group) {
      setWeeks(group.season_weeks ?? 10);
      setStartDate(group.season_start_date || "");
      setTracks(group.allowed_tracks && group.allowed_tracks.length > 0 ? group.allowed_tracks : ALL_TRACKS);
      setRaceNums(group.allowed_race_numbers && group.allowed_race_numbers.length > 0 ? group.allowed_race_numbers : RACE_NUMBERS_ALL);
    }
  }, [open, group]);

  const toggle = (arr, setArr, v) => {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/groups/${group.id}`, {
        season_weeks: Number(weeks),
        season_start_date: startDate || null,
        allowed_tracks: tracks,
        allowed_race_numbers: raceNums.map(Number).sort((a, b) => a - b),
      });
      toast.success("Competition settings saved");
      onSaved && onSaved(data.group);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Competition settings" testId="competition-settings-modal">
      <div className="space-y-5">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
            Season length: <span className="text-[#1971c2] font-black">{weeks} week{weeks === 1 ? "" : "s"}</span>
          </label>
          <input
            type="range" min={1} max={20} value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            className="w-full accent-[#1971c2]"
            data-testid="settings-season-weeks"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
            Season start date {group?.season_end_date && startDate && (
              <span className="text-[#6c757d] font-normal normal-case">
                (ends {group.season_end_date})
              </span>
            )}
          </label>
          <input
            type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1971c2] font-medium text-base"
            data-testid="settings-season-start"
          />
          <div className="text-[10px] text-[#6c757d] mt-1">Leave blank until you're ready. Races outside the season window are flagged as "Out of season".</div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
            Allowed tracks ({tracks.length}/{ALL_TRACKS.length})
          </label>
          <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto" data-testid="settings-tracks">
            {ALL_TRACKS.map((t) => {
              const on = tracks.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(tracks, setTracks, t)}
                  data-testid={`settings-track-${t}`}
                  className={`rounded-lg px-2 py-2 text-[11px] font-bold border transition-transform active:scale-95 text-left ${
                    on ? "bg-[#1971c2] text-white border-[#1971c2]" : "bg-white text-[#212529] border-[#e9ecef]"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
            Allowed race numbers
          </label>
          <div className="grid grid-cols-5 gap-2" data-testid="settings-race-numbers">
            {RACE_NUMBERS_ALL.map((n) => {
              const on = raceNums.includes(n);
              return (
                <button
                  key={n}
                  onClick={() => toggle(raceNums, setRaceNums, n)}
                  data-testid={`settings-race-num-${n}`}
                  className={`rounded-lg py-2 text-xs font-bold border transition-transform active:scale-95 ${
                    on ? "bg-[#1971c2] text-white border-[#1971c2]" : "bg-white text-[#212529] border-[#e9ecef]"
                  }`}
                >
                  R{n}
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save"
          className="w-full bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3.5 active:scale-95 transition-transform shadow-md disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </BottomSheet>
  );
}
